/**
 * SharePage — the PUBLIC landing for a shared video (app.vivideo.ai/share/:id).
 *
 * Rendered OUTSIDE the auth gate (see App.tsx) so anyone with the link can open it,
 * signed in or not. It loads the finished video by order id from the public `v2-share`
 * endpoint and embeds it via the `stream` action — so the shared link is the Vivideo
 * site, not the raw S3 file. The whole page is a soft conversion funnel: a beautiful,
 * on-brand frame around the video with clear, inviting CTAs into vivideo.ai and the app,
 * so a friend who receives the link gets curious and comes to make their own.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_CONFIG } from '@/api/config';
import { ResultVideoPlayer } from '@/v2/components/ResultVideoPlayer';

const LANDING = 'https://vivideo.ai/?utm_source=share&utm_medium=video&utm_campaign=v2_share';
const APP = 'https://app.vivideo.ai/?utm_source=share&utm_medium=video&utm_campaign=v2_share';

type Info = { title?: string; poster?: string | null; duration?: number | null; hasCaptions?: boolean };

export function SharePage() {
  const { t } = useTranslation('v2');
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [info, setInfo] = useState<Info>({});

  useEffect(() => {
    let alive = true;
    setState('loading');
    (async () => {
      try {
        const r = await fetch(`${API_CONFIG.baseUrl}/v2-share?order_id=${encodeURIComponent(id || '')}`);
        const d = await r.json();
        if (!alive) return;
        if (d?.ready) {
          setInfo({ title: d.title, poster: d.poster, duration: d.duration, hasCaptions: !!d.has_captions });
          setState('ready');
        } else {
          setState('unavailable');
        }
      } catch {
        if (alive) setState('unavailable');
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const streamBase = `${API_CONFIG.baseUrl}/v2-share?order_id=${encodeURIComponent(id || '')}&action=stream`;
  // Same renditions the in-app player uses: clean (cc=0) + captioned (cc=1) so the CC toggle works.
  const cleanUrl = `${streamBase}&cc=0`;
  const captionedUrl = `${streamBase}&cc=1`;
  const title = info.title && info.title.trim() ? info.title : t('share.defaultTitle');

  return (
    <div className="min-h-screen bg-[#0b0b11] text-text-primary overflow-x-hidden relative font-sans">
      {/* ambient brand glows */}
      <div className="pointer-events-none absolute -top-48 left-1/2 -translate-x-1/2 w-[130vw] h-[60vh] rounded-full opacity-50 blur-[130px] bg-[radial-gradient(ellipse_at_center,_rgba(139,92,246,0.34),_transparent_62%)]" />
      <div className="pointer-events-none absolute top-1/3 -right-40 w-[70vw] h-[55vh] rounded-full opacity-40 blur-[130px] bg-[radial-gradient(ellipse_at_center,_rgba(236,72,153,0.24),_transparent_62%)]" />

      {/* top bar */}
      <header className="relative z-10 max-w-5xl mx-auto flex items-center justify-between px-5 sm:px-8 py-5">
        <a href={LANDING} className="flex items-center gap-2.5 group" aria-label={t('share.vivideoHome')}>
          <img src="/logo.svg" alt="" className="w-8 h-8 drop-shadow-[0_2px_10px_rgba(139,92,246,0.6)]" />
          <span className="text-xl font-extrabold tracking-tight">Vivideo</span>
        </a>
        <a
          href={LANDING}
          className="px-4 py-2 rounded-full text-sm font-bold bg-white/10 hover:bg-white/[0.16] border border-white/15 backdrop-blur transition-colors"
        >
          {t('share.tryVivideo')} →
        </a>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 pt-3 pb-16">
        {/* eyebrow + title */}
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-primary-light mb-3">
            <img src="/logo.svg" alt="" className="w-3.5 h-3.5" />
            {t('share.madeWithVivideo')}
          </span>
          <h1 className="text-[1.65rem] sm:text-4xl font-extrabold leading-[1.12] tracking-tight">{title}</h1>
        </div>

        {/* video frame */}
        <div className="relative rounded-2xl overflow-hidden bg-black border border-white/10 shadow-[0_40px_110px_-30px_rgba(139,92,246,0.6)] aspect-video">
          {state === 'loading' && (
            <div className="absolute inset-0 grid place-items-center">
              <div className="w-9 h-9 rounded-full border-2 border-white/20 border-t-primary-light animate-spin" />
            </div>
          )}
          {state === 'ready' && (
            <ResultVideoPlayer
              src={info.hasCaptions ? cleanUrl : streamBase}
              burnedSrc={info.hasCaptions ? captionedUrl : undefined}
              poster={info.poster || undefined}
              autoPlay
            />
          )}
          {state === 'unavailable' && (
            <div className="absolute inset-0 grid place-items-center text-center px-8">
              <div>
                <p className="text-lg font-bold">{t('share.unavailableTitle')}</p>
                <p className="text-sm text-text-secondary mt-1.5">
                  {t('share.unavailableBody')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* curiosity pills */}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {[t('share.pillAvatars'), t('share.pillVoiceover'), t('share.pillCaptions'), t('share.pillNoEditing')].map((p) => (
            <span
              key={p}
              className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white/[0.06] border border-white/10 text-text-secondary"
            >
              {p}
            </span>
          ))}
        </div>

        {/* conversion CTA */}
        <section className="mt-12 text-center">
          <h2 className="text-2xl sm:text-[2rem] font-extrabold leading-tight mb-2.5">
            {t('share.ctaHeading')}
          </h2>
          <p className="text-text-secondary max-w-md mx-auto mb-7 leading-relaxed">
            {t('share.ctaBody')}
          </p>
          <a
            href={LANDING}
            className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-primary to-accent text-white text-base font-bold shadow-[0_14px_44px_-10px_rgba(236,72,153,0.65)] hover:-translate-y-0.5 hover:shadow-[0_18px_54px_-10px_rgba(236,72,153,0.8)] transition-all"
          >
            {t('share.ctaButton')}
            <span className="transition-transform group-hover:translate-x-0.5">→</span>
          </a>
          <p className="text-xs text-text-tertiary mt-4">
            {t('share.noCreditCard')}{' '}
            <a href={APP} className="underline decoration-white/30 underline-offset-2 hover:text-text-secondary">
              {t('share.openApp')}
            </a>
          </p>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/[0.08] py-6 text-center text-xs text-text-tertiary">
        <a href={LANDING} className="hover:text-text-secondary transition-colors font-semibold">
          vivideo.ai
        </a>
        <span className="mx-2">·</span>
        {t('share.madeWithVivideo')}
      </footer>
    </div>
  );
}

export default SharePage;
